@if (message().role === 'user') {
  <div class="ml-8 flex flex-col gap-0.5">
    @if (showContext() && message().context; as context) {
      <span
        data-testid="context-caption"
        class="text-text-tertiary text-2xs flex min-w-0 items-center gap-1">
        <i aria-hidden="true" class="shrink-0" [class]="contextIcons[context.kind]"></i>
        <app-assistant-context-text [summary]="context" />
      </span>
    }
    @if (editing()) {
      <div class="flex flex-col gap-1.5">
        <textarea
          #editBox
          rows="3"
          class="form-textarea max-h-[min(40vh,18rem)] resize-none text-sm"
          [attr.aria-label]="'assistant.chrome.edit' | translate"
          [value]="draft()"
          (input)="draft.set($any($event.target).value)"
          (keydown)="onEditKey($event)"></textarea>
        <div class="flex justify-end gap-1.5">
          <button
            type="button"
            data-testid="edit-cancel"
            class="btn btn-cancel btn-sm"
            (click)="cancelEdit()">
            {{ 'assistant.chrome.cancel' | translate }}
          </button>
          <button
            type="button"
            data-testid="edit-send"
            class="btn btn-primary btn-sm"
            [disabled]="!draft().trim()"
            (click)="sendEdit()">
            {{ 'assistant.chrome.send' | translate }}
          </button>
        </div>
      </div>
    } @else {
      <div
        class="bg-accent-interactive text-text-primary rounded-sm px-3 py-2 text-sm whitespace-pre-wrap">
        {{ message().text }}
      </div>
      <div
        class="flex items-center justify-end gap-0.5 transition-opacity"
        [class]="
          last()
            ? 'opacity-100'
            : 'opacity-0 group-focus-within/message:opacity-100 group-hover/message:opacity-100'
        ">
        <button
          type="button"
          data-testid="copy-message"
          class="btn btn-icon text-text-tertiary"
          [attr.aria-label]="'assistant.chrome.copy' | translate"
          [title]="'assistant.chrome.copy' | translate"
          (click)="copyMessage()">
          <i [class]="copied() ? ICONS.check : ICONS.copy"></i>
        </button>
        @if (assistant.canEdit(message().id)) {
          <button
            #editButton
            type="button"
            data-testid="edit-message"
            class="btn btn-icon text-text-tertiary"
            [attr.aria-label]="'assistant.chrome.edit' | translate"
            [title]="'assistant.chrome.edit' | translate"
            (click)="startEdit()">
            <i [class]="ICONS.pencil"></i>
          </button>
        }
      </div>
    }
  </div>
} @else if (message().role === 'notice') {
  <div class="text-text-tertiary flex items-center gap-2 text-xs" role="note">
    <span class="border-weak flex-1 border-t"></span>
    <span>{{ 'assistant.context_reset' | translate }}</span>
    <span class="border-weak flex-1 border-t"></span>
  </div>
} @else {
  <div class="flex flex-col gap-1.5">
    @if (readTools().length > 0) {
      <app-assistant-tool-group
        [tools]="readTools()"
        [streaming]="message().status === 'streaming'" />
    }
    @for (tool of changeTools(); track tool.callId) {
      <app-assistant-tool-line [tool]="tool" />
    }
    @for (run of message().runs ?? []; track run.optimizationId) {
      <app-assistant-run-card [run]="run" [parameterId]="parameterOf(run)" />
    }
    @for (file of message().files ?? []; track file.fileId) {
      <app-assistant-file-card [file]="file" />
    }
    @if (pendingCount() > 1) {
      <div class="text-text-secondary flex items-center gap-2 text-xs">
        <span class="flex-1 font-medium">
          {{ 'assistant.approvals.waiting' | translate: { count: pendingCount() } }}
        </span>
        <button
          type="button"
          class="btn btn-transparent"
          (click)="assistant.answerAllApprovals(message().id, false)">
          {{ 'assistant.approvals.reject_all' | translate }}
        </button>
        <button
          type="button"
          class="btn btn-cancel"
          (click)="assistant.answerAllApprovals(message().id, true)">
          {{ 'assistant.approvals.approve_all' | translate }}
        </button>
      </div>
    }
    @for (card of message().approvals ?? []; track card.approvalId) {
      <app-assistant-approval-card [card]="card" [messageId]="message().id" />
    }
    @if (message().text) {
      <!-- [innerHTML] runs Angular's sanitizer, which strips scripts and event handlers from model output. -->
      <div
        class="prose prose-sm text-text-primary prose-headings:text-text-primary prose-strong:text-text-primary prose-code:text-text-primary prose-a:text-accent-main prose-li:marker:text-text-tertiary prose-headings:tracking-tight prose-h1:text-base prose-h2:text-sm prose-h3:text-sm prose-h4:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none max-w-none"
        [class.assistant-streaming]="message().status === 'streaming'"
        [innerHTML]="html()"
        (click)="onContentClick($event)"
        (keydown)="onContentKey($event)"></div>
    } @else if (message().status === 'streaming') {
      <div class="text-text-tertiary text-xs">
        {{ 'assistant.thinking' | translate }}
      </div>
    }
    @if (
      message().stop &&
      message().stop !== 'end' &&
      message().stop !== 'cancelled' &&
      message().stop !== 'approval_required'
    ) {
      <div class="text-text-secondary text-xs">
        {{ 'assistant.stop_reasons.' + message().stop | translate }}
      </div>
    }
    @if (message().status === 'cancelled') {
      <div class="text-text-tertiary text-xs">
        {{ 'assistant.stop_reasons.cancelled' | translate }}
      </div>
    }
    @if (message().error; as error) {
      <div class="text-danger flex items-start gap-1.5 text-xs" role="alert">
        <i [class]="ICONS.warning"></i>
        <span>
          {{ 'assistant.errors.' + error.code | translate }}
          @if (error.code === 'bad_request') {
            <br />{{ error.message }}
          }
        </span>
      </div>
    }
    @if (message().status !== 'streaming') {
      <div
        class="flex items-center gap-0.5 transition-opacity"
        [class]="
          last()
            ? 'opacity-100'
            : 'opacity-0 group-focus-within/message:opacity-100 group-hover/message:opacity-100'
        ">
        <button
          type="button"
          data-testid="copy-message"
          class="btn btn-icon text-text-tertiary"
          [attr.aria-label]="'assistant.chrome.copy' | translate"
          [title]="'assistant.chrome.copy' | translate"
          (click)="copyMessage()">
          <i [class]="copied() ? ICONS.check : ICONS.copy"></i>
        </button>
        @if (last() && assistant.canRetry()) {
          <button
            type="button"
            data-testid="retry"
            class="btn btn-icon text-text-tertiary"
            [attr.aria-label]="'assistant.retry' | translate"
            [title]="'assistant.retry' | translate"
            (click)="assistant.retry()">
            <i [class]="ICONS.arrowsClockwise"></i>
          </button>
        }
      </div>
    }
  </div>
}
